import { Component, signal, viewChild, DestroyRef, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// MDB Imports
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbValidationModule } from 'mdb-angular-ui-kit/validation';
import { MdbStepperModule, MdbStepperComponent } from 'mdb-angular-ui-kit/stepper';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { MdbFileUploadComponent, MdbFileUploadModule } from 'mdb-angular-file-upload';
import { MdbCheckboxModule } from 'mdb-angular-ui-kit/checkbox';
import { MdbRadioModule } from 'mdb-angular-ui-kit/radio';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';

//Services
import { UserContactDetailsRequest, UserService } from '@core/services/user';
import { CreateServiceRequestRequest, ServiceRequestService } from '@core/services/service-request';
import { Gradientsegmentbar } from '@shared/components/gradientsegmentbar/gradientsegmentbar';



@Component({
  selector: 'app-service-requests',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MdbFormsModule,
    MdbValidationModule,
    MdbStepperModule,
    MdbSelectModule,
    MdbFileUploadModule,
    MdbCheckboxModule,
    MdbRadioModule,
    MdbRippleModule,
    Gradientsegmentbar
  ],
  templateUrl: './service-requests.html',
  styleUrls: ['./service-requests.scss']
})
export class ServiceRequests {
  //file upload constants
  // Keep the attachment total below the API/IIS request limit so there is room
  // for multipart boundaries and the non-file service-request fields.
  static readonly MAX_TOTAL_FILE_SIZE_MB = 95;
  static readonly MAX_SINGLE_FILE_SIZE_MB = 25;
  static readonly MAX_NUMBER_FILES = 25;
  maxSingleFileSize = ServiceRequests.MAX_SINGLE_FILE_SIZE_MB;
  maxTotalFileSize = ServiceRequests.MAX_TOTAL_FILE_SIZE_MB;
  maxNumberFiles = ServiceRequests.MAX_NUMBER_FILES;

  // Dependency injection using inject()
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private userService = inject(UserService);
  //overlap of name
  private serviceRequestService = inject(ServiceRequestService);

  // Modern viewChild signal query
  stepper = viewChild.required<MdbStepperComponent>('stepper');

  // Responsive behavior
  screenWidth = signal(typeof window !== 'undefined' ? window.innerWidth : 1024);
  isMobile = computed(() => this.screenWidth() < 768);
  stepperOrientation = computed(() => this.isMobile() ? 'horizontal' : 'vertical');

  // Signal-based state
  referenceCode = signal<string>('');
  isSubmitting = signal(false);
  isSubmitted = signal(false);
  submissionError = signal<string>('');
  missingAttachmentConfirmation = signal(false);
  userName = signal<string>('');
  //file upload file list
  fileUpload = viewChild<MdbFileUploadComponent>('fileUpload');
  uploadedFiles = signal<File[]>([]);
  uploadedFilesTotalPercentage = signal<number>(0);
  
  // Form groups
  caseTypeForm!: FormGroup;
  relatedCaseForm!: FormGroup;
  serviceCategoryForm!: FormGroup;
  serviceDescriptionForm!: FormGroup;
  attachmentsForm!: FormGroup;
  contactInfoForm!: FormGroup;
  attestReviewSubmitForm!: FormGroup;

  // User contact info signal
  userContactInfo = signal<{ email?: string; phone?: string; fullName?: string; }>({});

  // Only offer contact methods the user actually has on file
  private readonly contactMethods = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'Text Message (SMS)' }
  ];

  availableContactMethods = computed(() => {
    const info = this.userContactInfo();
    const hasEmail = !!info.email?.trim();
    const hasPhone = !!info.phone?.trim();

    return this.contactMethods.filter(method =>
      (method.value === 'email' && hasEmail) ||
      (method.value === 'sms' && hasPhone)
    );
  });

  constructor() {
    this.initializeForms();
    this.loadUserContactInfo();
    this.setupResizeListener();
  }

  private setupResizeListener(): void {
    if (typeof window !== 'undefined') {
      const resizeHandler = () => {
        this.screenWidth.set(window.innerWidth);
      };

      window.addEventListener('resize', resizeHandler);

      // Cleanup on destroy
      effect(() => {
        return () => {
          window.removeEventListener('resize', resizeHandler);
        };
      });
    }
  }

  private loadUserContactInfo(): void {
    //pull current info from storage
    const userInfo = this.userService.getUserInfoFromStorage();
    if (userInfo) {
      this.userContactInfo.set({
        email: userInfo.email,
        phone: userInfo.phoneNumber,
        fullName: `${userInfo.firstName} ${userInfo.lastName}`,
      });
    }
    //build user request
    const getUserRequest: UserContactDetailsRequest = {
      email: userInfo?.email ?? '',
      phoneNumber: userInfo?.phoneNumber ?? '',
    };

    this.userService.getUserInfo(getUserRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.contactInfoForm.patchValue({
          zipcode: user?.zipCode ?? '',
          language: user?.preferredLanguage || 'English'
        });
      });

    this.applyDefaultContactMethod();
  }

  //if only 1 contact method exists for the user always default to that method
  private applyDefaultContactMethod(): void {
    const available = this.availableContactMethods();
    if (available.length === 1) {
      this.preferredContact.setValue(available[0].value);
    }
  }

  //#region Form Initialization
  private initializeForms(): void {
    // Step 1: Case Type
    this.caseTypeForm = new FormGroup({
      caseType: new FormControl('', [Validators.required])
    });

    // Step 2: Related Case (optional)
    this.relatedCaseForm = new FormGroup({
      relatedCase: new FormControl('')
    });

    // Step 3: Service Category / Details
    this.serviceCategoryForm = new FormGroup({
      selectedCategory: new FormControl('', [Validators.required]),
      // Document Preparation fields
      docPrepFormType: new FormControl(''),
      // Form Review fields
      formReviewType: new FormControl('')
    });

    this.caseType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.canSelectDocumentPreparation && this.selectedCategory.value === 'docPrep') {
          this.selectedCategory.setValue('');
        }
      });

    // Use takeUntilDestroyed for automatic cleanup
    this.serviceCategoryForm.get('selectedCategory')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(category => {
        this.missingAttachmentConfirmation.set(false);
        this.updateCategoryValidation(category);
      });

    // Step 4: Description
    this.serviceDescriptionForm = new FormGroup({
      description: new FormControl('', [Validators.required])
    });

    // Step 5: Attachments (optional)
    this.attachmentsForm = new FormGroup({
      hasAttachments: new FormControl(false),
      totalSizeWithinLimit: new FormControl(true, Validators.requiredTrue)
    });

    // Step 6: Contact Info
    this.contactInfoForm = new FormGroup({
      zipcode: new FormControl('', [
        Validators.required,
        Validators.pattern(/^\d{5}$/)
      ]),
      preferredContact: new FormControl('', [Validators.required]),
      language: new FormControl('English'),
      visitedBefore: new FormControl('No'),
    });

    // Step 7: Review/Submit 
    this.attestReviewSubmitForm = new FormGroup({
      attestNoAttorneyCheck: new FormControl(false, Validators.requiredTrue),
      attestFormsCompleteCheck: new FormControl(false, Validators.requiredTrue),
      attestTrueAndAccurateFormCheck: new FormControl(false, Validators.requiredTrue),
      attestComplySelfHelpCheck: new FormControl(false, Validators.requiredTrue),
      // Keep the linear stepper from opening Confirmation until the API call succeeds.
      submissionComplete: new FormControl(false, Validators.requiredTrue)
    });
  }
  //#endregion


  //update the validators as the radio control for category changes
  private updateCategoryValidation(category: string): void {
    const docPrepFormType = this.serviceCategoryForm.get('docPrepFormType');
    const formReviewType = this.serviceCategoryForm.get('formReviewType');

    // Clear ALL validators first
    docPrepFormType?.clearValidators();
    formReviewType?.clearValidators();

    // Reset ALL values and mark as untouched
    docPrepFormType?.setValue('');
    formReviewType?.setValue('');

    docPrepFormType?.markAsUntouched();
    formReviewType?.markAsUntouched();

    // Now set validators based on selected category
    if (category === 'docPrep') {
      docPrepFormType?.setValidators([Validators.required]);
    } else if (category === 'formReview') {
      formReviewType?.setValidators([Validators.required]);
    }
    // Update validity for all fields
    docPrepFormType?.updateValueAndValidity();
    formReviewType?.updateValueAndValidity();
  }
  //#region Getters
  // Getters for template access
  get caseType() { return this.caseTypeForm.get('caseType')!; }
  get canSelectDocumentPreparation(): boolean {
    return this.caseType.value !== 'landLord-tenant' && this.caseType.value !== 'small-claims';
  }
  get relatedCase() { return this.relatedCaseForm.get('relatedCase')!; }
  get selectedCategory() { return this.serviceCategoryForm.get('selectedCategory')!; }
  get docPrepFormType() { return this.serviceCategoryForm.get('docPrepFormType')!; }
  get formReviewType() { return this.serviceCategoryForm.get('formReviewType')!; }
  get description() { return this.serviceDescriptionForm.get('description')!; }
  get zipcode() { return this.contactInfoForm.get('zipcode')!; }
  get preferredContact() { return this.contactInfoForm.get('preferredContact')!; }
  get language() { return this.contactInfoForm.get('language')!; }
  get visitedBefore() { return this.contactInfoForm.get('visitedBefore')!; }
  get attestNoAttorneyCheck() { return this.attestReviewSubmitForm.get('attestNoAttorneyCheck')!; }
  get attestFormsCompleteCheck() { return this.attestReviewSubmitForm.get('attestFormsCompleteCheck')!; }
  get attestTrueAndAccurateFormCheck() { return this.attestReviewSubmitForm.get('attestTrueAndAccurateFormCheck')!; }
  get attestComplySelfHelpCheck() { return this.attestReviewSubmitForm.get('attestComplySelfHelpCheck')!; }
  //#endregion

  //#region Form Submission
  // Get complete form data for review
  //Needed for the Form Summary 
  getCompleteFormData() {
    return {
      caseType: this.caseTypeForm.value.caseType,
      relatedCase: this.relatedCaseForm.value.relatedCase,
      category: this.serviceCategoryForm.value.selectedCategory,
      // Document Preparation fields
      docPrepFormType: this.serviceCategoryForm.value.docPrepFormType,
      // Form Review fields
      formReviewType: this.serviceCategoryForm.value.formReviewType,
      description: this.serviceDescriptionForm.value.description,
      zipcode: this.contactInfoForm.value.zipcode,
      preferredContact: this.contactInfoForm.value.preferredContact,
      language: this.contactInfoForm.value.language,
      visitedBefore: this.contactInfoForm.value.visitedBefore,
      contactDisplay: this.getContactDisplay()
    };
  }

  // Submit the request
  submitServiceRequest(proceedWithoutAttachment = false): void {
    if (this.isSubmitted() || this.isSubmitting() || !this.canSubmitServiceRequest()) {
      if (this.attachmentsForm.invalid) {
        this.announceToScreenReader(
          `The total file size exceeds ${this.maxTotalFileSize} megabytes. Remove one or more files before submitting.`
        );
      }
      return;
    }

    if (this.selectedCategory.value === 'formReview' &&
        this.uploadedFiles().length === 0 &&
        !proceedWithoutAttachment) {
      this.missingAttachmentConfirmation.set(true);
      return;
    }

    this.missingAttachmentConfirmation.set(false);
    this.submissionError.set('');
    this.isSubmitting.set(true);

    const formData = this.buildFormData();

    this.serviceRequestService.createServiceRequest(formData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.referenceCode.set(this.formatServiceRequestId(response.serviceRequestId));
          this.attestReviewSubmitForm.get('submissionComplete')?.setValue(true);
          this.isSubmitting.set(false);
          // Prevent completed steps from being reopened and submitted again.
          this.isSubmitted.set(true);
          this.stepper().next();
        },
        error: (error: HttpErrorResponse) => {
          this.isSubmitting.set(false);
          const message = error.status === 413
            ? `The request is too large for the server. Remove one or more files and keep the combined file size below ${this.maxTotalFileSize}MB before trying again.`
            : 'We could not submit your service request. Please try again.';
          this.submissionError.set(message);
          this.announceToScreenReader(message);
        }
      });
  }

  returnToAttachments(): void {
    this.missingAttachmentConfirmation.set(false);
    this.stepper().setNewActiveStep(4);
  }

  private buildFormData(): FormData {
    const formData = new FormData();

    const user = this.userService.getUserInfoFromStorage();
    const categoryForm = this.serviceCategoryForm.value;

    // Basic Info
    formData.append('UserId', (user?.userId ?? 0).toString());
    formData.append('UserName', `${user?.firstName} ${user?.lastName}`);
    formData.append('Email', user?.email ?? '');
    formData.append('PhoneNumber', user?.phoneNumber ?? '');

    // Case Info
    formData.append('CaseNumber', this.relatedCaseForm.value.relatedCase ?? '');
    formData.append('CaseType', this.getCaseTypeDisplay(this.caseTypeForm.value.caseType));
    formData.append('SubCaseType', '');

    // Category + Form Type
    formData.append('Category', this.getCategoryDisplay(categoryForm.selectedCategory));
    formData.append('FormType', this.getFormTypeDisplay(categoryForm.docPrepFormType || categoryForm.formReviewType));

    // Description
    formData.append('Description', this.serviceDescriptionForm.value.description ?? '');

    // Contact
    formData.append('PreferredContact', this.contactInfoForm.value.preferredContact ?? '');
    formData.append('PreferredLanguage', this.contactInfoForm.value.language ?? '');
    formData.append('VisitedBefore', this.contactInfoForm.value.visitedBefore ?? '');
    formData.append('ZipCode', this.contactInfoForm.value.zipcode ?? '');

    // Metadata
    formData.append('Status', 'New');
    formData.append('CreatedBy', (user?.userId ?? 0).toString());

    // FILES
    this.uploadedFiles().forEach((file, index) => {
      formData.append(`ServiceRequestDocuments[${index}].File`, file);
      formData.append(`ServiceRequestDocuments[${index}].Description`, file.name);
    });

      return formData;
  }
  //#endregion

  //#region File Upload
  getFileExtension(file: File): string {
    const ext = file.name.split('.').pop()?.toUpperCase();
    return ext || 'FILE';
  }
  //PREVENT re-adding files / triggering FileAdded when updating file list with the upload plugin
  private isSyncing = false;
  // Enhanced file addition with screen reader announcement
  onFileAdded(files: File[]): void {
    //Ignore the event if we are syncing the file list between the plugin and our own uploadedFiles list 
    if (this.isSyncing) {
      return;
    }

    const currentFiles = this.uploadedFiles();
    const acceptedFiles = files.filter((file, index) =>
      !currentFiles.some(existing => this.areSameFile(existing, file)) &&
      !files.slice(0, index).some(existing => this.areSameFile(existing, file))
    );
    const duplicateCount = files.length - acceptedFiles.length;

    this.uploadedFiles.set([...currentFiles, ...acceptedFiles]);
    this.updateTotalPercentage();

    // Announce to screen readers
    const addedMessage = acceptedFiles.length === 1
      ? `${acceptedFiles[0].name} added.`
      : `${acceptedFiles.length} files added.`;
    const duplicateMessage = duplicateCount > 0
      ? ` ${duplicateCount} duplicate ${duplicateCount === 1 ? 'file was' : 'files were'} ignored.`
      : '';
    const message =
      `${addedMessage}${duplicateMessage} Total: ${this.uploadedFiles().length} files.`;
    this.announceToScreenReader(message);

    if (duplicateCount > 0) {
      this.syncFilesToPlugin();
    }
  }

  private areSameFile(first: File, second: File): boolean {
    return first.name === second.name &&
      first.size === second.size &&
      first.lastModified === second.lastModified;
  }

  onFileRemoved(file: File): void {
    if (this.isSyncing) {
      return;
    }
    this.uploadedFiles.update(current =>
      current.filter(f => f.name !== file.name || f.size !== file.size)
    );
    this.updateTotalPercentage();
  }

  // Enhanced file removal with screen reader announcement
  removeFile(file: File): void {
    const fileName = file.name;
    this.uploadedFiles.update(current => current.filter(f => f !== file));
    this.updateTotalPercentage();
    //update the plugin to have the same files
    this.syncFilesToPlugin();
    // Announce to screen readers
    this.announceToScreenReader(`${fileName} removed. ${this.uploadedFiles().length} files remaining.`);
  }

  //Issues with the drag and drop plugin on mobile when a user attempts to upload MANY files.
  //opting to maintain a file list below the plugin to allow for easier removal of files
  private syncFilesToPlugin(): void {
    const component = this.fileUpload();
    if (!component) {
      return;
    }

    // set flag before sync so onFileAdded ignores the incoming event
    this.isSyncing = true;

    const input = component.input.nativeElement;

    // reset the plugin's internal state entirely
    component.reset();

    // Then rebuild with remaining files
    const dt = new DataTransfer();
    this.uploadedFiles().forEach(f => dt.items.add(f));
    input.files = dt.files;

    // Now the change event adds to a clean slate instead of appending
    input.dispatchEvent(new Event('change', { bubbles: true }));

    // reset flag after completing sync
    this.isSyncing = false;
  }

  // Screen reader announcement utility
  private announceToScreenReader(message: string): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'visually-hidden';
    announcement.textContent = message;
    document.body.appendChild(announcement);

    setTimeout(() => announcement.remove(), 1000);
  }
  //file.size is in BYTES
  getTotalFileSizeBytes(): number {
    return this.uploadedFiles().reduce((total, file) => total + file.size, 0);
  }

  updateTotalPercentage(): void {
    const totalBytes = this.getTotalFileSizeBytes();
    //convert the configured file size from MB to B
    const maxBytes = this.maxTotalFileSize * 1024 * 1024;
    const percentage = Math.round((totalBytes / maxBytes) * 100);
    this.uploadedFilesTotalPercentage.set(percentage);

    const isWithinLimit = totalBytes <= maxBytes;
    const totalSizeControl = this.attachmentsForm.get('totalSizeWithinLimit');
    totalSizeControl?.setValue(isWithinLimit, { emitEvent: false });

    if (!isWithinLimit) {
      totalSizeControl?.markAsTouched();
      this.announceToScreenReader(
        `The total file size exceeds ${this.maxTotalFileSize} megabytes. Remove one or more files before continuing.`
      );
    }
  }

  isTotalFileSizeExceeded(): boolean {
    return this.attachmentsForm.get('totalSizeWithinLimit')?.invalid ?? false;
  }

  getCappedUploadPercentage(): number {
    return Math.min(this.uploadedFilesTotalPercentage(), 100);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
  //#endregion

  //#region Link Case
  // Link case functionality
  linkCase(): void {
    // TODO: Open modal or navigate to case linking
    console.log('Link case clicked');
  }
  //#endregion
  // Navigate to service request history
  viewServiceRequests(): void {
    this.router.navigate(['/service-request-history']);
  }

  //#region Utility
  canSubmitServiceRequest(): boolean {
    return this.attachmentsForm.valid
      && this.attestNoAttorneyCheck.valid
      && this.attestFormsCompleteCheck.valid
      && this.attestTrueAndAccurateFormCheck.valid
      && this.attestComplySelfHelpCheck.valid;
  }

  private formatServiceRequestId(serviceRequestId: number): string {
    return `SR-${serviceRequestId.toString().padStart(4, '0')}`;
  }


  private formatPhoneNumber(phone: string): string {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Format as (555) 555-5555
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // Return original if not 10 digits
    return phone;
  }

  // Get contact display value from stored user info
  getContactDisplay(): string {
    const info = this.userContactInfo();
    const method = this.preferredContact.value;

    if (method === 'email' && info.email) {
      return info.email;
    }
    if (method === 'sms' && info.phone) {
      return this.formatPhoneNumber(info.phone);
    }
    return 'No contact information on file - please update your contact information on your profile';
  }

  getFullName(): string {
    const info = this.userContactInfo();
    return info.fullName ?? '';
  }

  // MDB's linear stepper blocks next() on an invalid stepForm but doesn't mark
  // anything touched, so the user gets a dead button with no visible errors.
  nextStep(stepper: MdbStepperComponent, form: FormGroup): void {
    if (form.invalid) {
      form.markAllAsTouched();
      this.announceToScreenReader('Please complete the required fields before continuing.');
      return;
    }

    stepper.next();
  }
  //#endregion

  //#region Form Dropdown Requests

  //TEMP
  //TODO: use api to get IDs from Lookup table
  private getCaseTypeId(value: string): number {
    const map: Record<string, number> = {
      'guardianship': 7,        // GUA
      'family-law': 9,          // FAM
      'child-support': 10,      // CS
      'landLord-tenant': 11,    // LLT
      'small-claims': 12,       // SC
      'care': 13,               // CARE100
      'unknown': 0              // TODO: confirm with backend what ID to use for unknown
    };
    return map[value] ?? 0;
  }

  private getCategoryId(value: string): number {
    const map: Record<string, number> = {
      'docPrep': 14,           // DOCPREP
      'formReview': 15,        // FORMREV
      'communication': 16,     // ACCTISU
      'question': 17           // ASKQ
    };
    return map[value] ?? 0;
  }

  private getFormTypeId(value: string): number {
    const map: Record<string, number> = {
      'petition': 18,          // PET
      'response': 19,          // RES
      'order': 20,             // ORD
      'other': 21,              // OTHR
      'na': 28 //Not applicable
    };
    return map[value] ?? 28;
  }



  // Display formatters
  getCategoryDisplay(value: string): string {
    const map: Record<string, string> = {
      'docPrep': 'Document Preparation',
      'formReview': 'Form Review',
      'communication': 'Account Issue / Verification',
      'question': 'Ask a Question'
    };
    return map[value] || value;
  }

  getCaseTypeDisplay(value: string): string {
    const map: Record<string, string> = {
      'guardianship': 'Guardianship',
      'family-law': 'Family Law',
      'child-support': 'Child Support',
      'landLord-tenant': 'Landlord/Tenant',
      'small-claims': 'Small Claims',
      'care': 'CARE',
      'unknown': 'I don\'t know'
    };
    return map[value] || value;
  }

  getFormTypeDisplay(value: string): string {
    const map: Record<string, string> = {
      'petition': 'Start Case',
      'response': 'Respond to Case',
      'order': 'Request an Order',
      'other': 'Other'
    };
    return map[value] || value;
  }

  getContactMethodDisplay(value: string): string {
    return value === 'email' ? 'Email' : 'Text Message';
  }
  //#endregion
}
